import { Col, Result, Row } from "antd"
import { Component, ErrorInfo, PropsWithChildren } from "react"

type Props = PropsWithChildren
type State = { errorInfo?: ErrorInfo; hasError: boolean }

// Check if an error is a chunk loading error
const isChunkLoadError = (error: Error): boolean => {
  const errorMessage = error.message.toLowerCase()
  const errorName = error.name.toLowerCase()

  return (
    errorName.includes("chunk") ||
    errorMessage.includes("chunk") ||
    errorMessage.includes("loading chunk") ||
    errorMessage.includes("failed to fetch dynamically imported module") ||
    errorMessage.includes("importing a module script failed")
  )
}

class ErrorBoundary<T extends Props> extends Component<T, State> {
  private error?: Error
  private reloadAttempted = false

  constructor(props: T) {
    super(props)
    this.state = { hasError: false }
  }

  static getDerivedStateFromError(): State {
    // Update state so the next render will show the fallback UI.
    return { hasError: true }
  }

  componentDidCatch(error: Error, errorInfo: ErrorInfo) {
    this.error = error

    // Check if this is a chunk loading error
    if (isChunkLoadError(error) && !this.reloadAttempted) {
      this.reloadAttempted = true

      // Only reload if we haven't already tried recently (prevent reload loops)
      const lastReloadAttempt = sessionStorage.getItem("chunkReloadAttempt")
      const reloadCount = parseInt(sessionStorage.getItem("chunkReloadCount") || "0", 10)
      const now = Date.now()

      // Maximum retry limit - prevent infinite loops
      const MAX_RELOAD_ATTEMPTS = 3
      if (reloadCount >= MAX_RELOAD_ATTEMPTS) {
        console.warn("Chunk reload limit reached. Stopping automatic reloads.")
        sessionStorage.removeItem("chunkReloadAttempt")
        sessionStorage.removeItem("chunkReloadCount")
        return
      }

      if (lastReloadAttempt) {
        const timeSinceLastAttempt = now - parseInt(lastReloadAttempt, 10)
        // If we tried to reload less than 5 seconds ago, don't try again
        if (timeSinceLastAttempt < 5000) {
          return
        }
      }

      // Increment reload count
      sessionStorage.setItem("chunkReloadCount", (reloadCount + 1).toString())

      // Mark that we're attempting a reload
      sessionStorage.setItem("chunkReloadAttempt", now.toString())

      // Clear the flags after 30 seconds to allow future reloads if needed
      setTimeout(() => {
        sessionStorage.removeItem("chunkReloadAttempt")
        sessionStorage.removeItem("chunkReloadCount")
      }, 30000)

      // Reload the page to get the latest chunks
      window.location.reload()
      return
    }

    // You can also log the error to an error reporting service
    console.error(error.message, errorInfo)
  }

  render() {
    if (this.state.hasError) {
      // If it's a chunk loading error and we're attempting to reload, show a minimal message
      if (this.error && isChunkLoadError(this.error) && this.reloadAttempted) {
        return (
          <Row justify={"center"} style={{ paddingTop: 100 }}>
            <Col xs={24} sm={20} md={12}>
              <Result
                title='Updating application...'
                subTitle='Please wait while we load the latest version.'
                status='info'
              />
            </Col>
          </Row>
        )
      }

      // You can render any custom fallback UI
      return (
        <Row justify={"center"}>
          <Col xs={24} sm={20} md={12}>
            <Result title={this.error?.name} subTitle={this.error?.message} status='error' />
          </Col>
        </Row>
      )
    }

    return this.props.children
  }
}

export { ErrorBoundary }
